import { FPO_ERROR_COPY_MAX_LENGTH } from '@/constants/fpoErrorCopy';
import { type PromoScheduleWindow } from '@/types/errors';

/**
 * TEAM-15229: the sentence that tells a diner **when** a recurring promo code works, built from the
 * schedule chowly-api (TEAM-15227) puts in an `outside_schedule` rejection's `additional_data`.
 *
 * The wire contract (TEAM-15226): `recurring_days` holds lowercase English day names, stored
 * sunday-first; `recurring_time_windows` holds up to six `{ start_time, end_time }` in zero-padded
 * 24-hour `HH:MM`, half-open, never crossing midnight. An `end_time` of `'23:59'` is the end-of-day
 * marker and runs through midnight (TEAM-15227); chowly-api normalises a submitted `'00:00'` end to
 * it. `'24:00'`, the marker TEAM-15226 used first, is still read as end of day. `null` means every
 * day / all day.
 *
 * **Nothing from the wire is interpolated.** The result reaches the global toast's
 * `dangerouslySetInnerHTML`, so day names are looked up in a whitelist and times are re-rendered from
 * validated digits. Anything that does not validate returns `undefined`, and the caller shows the
 * fixed fallback line — never a partial or guessed schedule.
 *
 * @see docs/DINER_ERROR_MESSAGES.md — the display rules and examples
 */

/** The single source of the days. Insertion order is display order: Monday first. */
const DAY_NAME = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
} as const;

type Day = keyof typeof DAY_NAME;

const DAYS = Object.keys(DAY_NAME) as Day[];

/** Zero-padded 24-hour wall clock, `00:00`–`23:59`. */
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * TEAM-15226's end-of-day marker, the `'23:59'` chowly-api uses for end of day everywhere. As an
 * `end_time` it runs through midnight (TEAM-15227), so it reads "midnight", never "11:59 PM".
 */
const END_OF_DAY = '23:59';
/**
 * The end-of-day marker TEAM-15226 used before it settled on `'23:59'`. chowly-api no longer sends it;
 * it is still accepted as an `end_time` and normalised to `END_OF_DAY`, so an old payload still reads
 * "midnight". Never handed to date-fns, whose `parse` yields Invalid Date.
 */
const LEGACY_END_OF_DAY = '24:00';
const START_OF_DAY = '00:00';

/** The smallest run of consecutive days written as a range rather than listed. */
const MIN_RANGE_RUN = 3;

function isDay(value: unknown): value is Day {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(DAY_NAME, value);
}

/**
 * The valid days in Monday-first order, `null` for "every day" (absent, empty, or all seven), or
 * `undefined` when the value is not a well-formed list of distinct day names.
 */
function parseDays(value: unknown): Day[] | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  if (!Array.isArray(value) || !value.every(isDay) || new Set(value).size !== value.length) {
    return undefined;
  }
  if (value.length === 0 || value.length === DAYS.length) {
    return null;
  }
  return DAYS.filter((day) => value.includes(day));
}

function isWindow(value: unknown): value is PromoScheduleWindow {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const { start_time: start, end_time: end } = value as Record<string, unknown>;
  return (
    typeof start === 'string' &&
    typeof end === 'string' &&
    HH_MM.test(start) &&
    (HH_MM.test(end) || end === LEGACY_END_OF_DAY) &&
    // Zero-padded, so lexicographic order is chronological: a '23:59' end admits every earlier start,
    // and '24:00' sorts after every HH:MM, so a legacy '23:59'–'24:00' still validates.
    start < end
  );
}

/**
 * The windows in chronological order with repeats dropped and overlapping or touching windows merged,
 * `null` for "all day" (absent, empty, or windows that together span the whole day), or `undefined`
 * when any entry is malformed.
 *
 * Merging keeps the sentence honest about when the code actually works: windows are half-open, so
 * `11:00–14:00` + `14:00–17:00` is one continuous `11:00–17:00`, and a repeated or contained window
 * adds nothing. The backend rejects overlaps (TEAM-15226), but this sentence must not repeat or
 * contradict itself if one slips through.
 */
function parseWindows(value: unknown): PromoScheduleWindow[] | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  if (!Array.isArray(value) || !value.every(isWindow)) {
    return undefined;
  }
  // One end-of-day marker from here on, so the merge, the all-day check and the formatter compare
  // against a single value. Normalised only after `isWindow` has checked `start < end` on the raw
  // value, which is what lets a legacy '23:59'–'24:00' through.
  const windows = value.map(({ start_time, end_time }) => ({
    start_time,
    end_time: end_time === LEGACY_END_OF_DAY ? END_OF_DAY : end_time,
  }));
  // Zero-padded HH:MM compares chronologically as strings, and END_OF_DAY sorts at or after every
  // start. A window ending END_OF_DAY therefore absorbs every window sorted after it, which is right:
  // it runs through midnight.
  const sorted = windows.sort((a, b) => (a.start_time < b.start_time ? -1 : 1));
  const merged: PromoScheduleWindow[] = [];
  sorted.forEach(({ start_time, end_time }) => {
    const last = merged[merged.length - 1];
    if (last && start_time <= last.end_time) {
      last.end_time = end_time > last.end_time ? end_time : last.end_time;
    } else {
      merged.push({ start_time, end_time });
    }
  });
  if (
    merged.length === 0 ||
    (merged.length === 1 &&
      merged[0].start_time === START_OF_DAY &&
      merged[0].end_time === END_OF_DAY)
  ) {
    return null;
  }
  return merged;
}

/** `'17:30'` → `'5:30 PM'`, `'00:00'` → `'12:00 AM'`, `'23:59'` → `'11:59 PM'`. */
function formatScheduleTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${meridiem}`;
}

/** `['Mondays']` → `'Mondays'`; `['A', 'B', 'C']` → `'A, B and C'`. */
function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) {
    return parts.join('');
  }
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function formatDays(days: Day[]): string {
  // Split into runs of consecutive days (Monday-first, no wrap from Sunday back to Monday).
  const runs: Day[][] = [];
  days.forEach((day) => {
    const lastRun = runs[runs.length - 1];
    const previous = lastRun?.[lastRun.length - 1];
    if (previous && DAYS.indexOf(day) === DAYS.indexOf(previous) + 1) {
      lastRun.push(day);
    } else {
      runs.push([day]);
    }
  });

  const parts = runs.flatMap((run) =>
    run.length >= MIN_RANGE_RUN
      ? [`${DAY_NAME[run[0]]}–${DAY_NAME[run[run.length - 1]]}`]
      : run.map((day) => `${DAY_NAME[day]}s`),
  );
  return joinWithAnd(parts);
}

/**
 * An end time: `END_OF_DAY` runs through midnight, so it reads `'midnight'`. Only an end gets this —
 * a `'23:59'` start (possible only in a legacy `'23:59'`–`'24:00'` window) is a clock time.
 */
function formatScheduleEndTime(time: string): string {
  return time === END_OF_DAY ? 'midnight' : formatScheduleTime(time);
}

function formatWindows(windows: PromoScheduleWindow[]): string {
  return joinWithAnd(
    windows.map(
      ({ start_time, end_time }) =>
        `${formatScheduleTime(start_time)} – ${formatScheduleEndTime(end_time)}`,
    ),
  );
}

/**
 * Build the diner-facing "when this code works" sentence.
 *
 * @returns the sentence, or `undefined` when the schedule is missing, malformed, says nothing (every
 *   day, all day), or would not fit a toast — the caller then shows the fixed fallback line.
 */
export function formatPromoScheduleMessage(
  recurringDays: unknown,
  recurringTimeWindows: unknown,
): string | undefined {
  const days = parseDays(recurringDays);
  const windows = parseWindows(recurringTimeWindows);
  if (days === undefined || windows === undefined || (days === null && windows === null)) {
    return undefined;
  }

  const daysPart = days ? formatDays(days) : 'daily';
  const message = windows
    ? `This code is valid ${daysPart}, ${formatWindows(windows)}.`
    : `This code is valid ${daysPart}.`;

  return message.length <= FPO_ERROR_COPY_MAX_LENGTH ? message : undefined;
}
